import { Portal } from "@radix-ui/react-portal";
import { X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useRef, useState } from "react";
import { Sheet } from "@/components/ui/sheet";
import { TestPolicy } from "./TestPolicy";
import { Input } from "@/components/ui/input";
import {
  ExpensePoliciesDocument,
  useAdminDepartmentsQuery,
  useUpsertPolicyMutation,
} from "@common/utils/graphql";
import {
  DepartmentOption,
  SelectDepartment,
  entireCompany,
} from "@common/components/SelectDepartment";
import { useToast } from "@/components/ui/use-toast";
import { Policy } from "./ExpensePolicy";
import { SuggestionBox } from "./SuggesionBox";
import { TemplateSection } from "./TemplateSection";

const placheholder = `Write your initial policy in plain English and we will turn it into a comprehensive policy, for example:

Purpose: Explain the purpose of the policy.
Scope: Define who the policy applies to.
Eligibility: Detail the eligibility criteria for receiving the stipend.`;

const getInitialDepartments = (editPolicy?: Policy) => () => {
  let initDepartments: DepartmentOption[] = [];
  if (editPolicy) {
    initDepartments = editPolicy.departments;
    if (initDepartments.length === 0) {
      initDepartments = [entireCompany];
    }
  }
  return initDepartments;
};

type Props = {
  onClose: () => void;
  editPolicy?: Policy;
  policies: Policy[];
};

export const ExpensePolicyForm = ({ onClose, editPolicy, policies }: Props) => {
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isTestPolicyOpen, setIsTestPolicyOpen] = useState(false);
  const [policy, setPolicy] = useState(editPolicy?.policy || "");
  const [policyName, setPolicyName] = useState(editPolicy?.name || "");
  const [selectedDepartments, setSelectedDepartments] = useState<
    DepartmentOption[]
  >(getInitialDepartments(editPolicy));
  const { data: departmentsData } = useAdminDepartmentsQuery();
  const [upsertPolicy, { loading }] = useUpsertPolicyMutation({
    refetchQueries: [ExpensePoliciesDocument],
  });

  const appentText = (text: string) => {
    setPolicy((prev) => (prev ? `${prev}\n\n${text}` : text));
    if (!textareaRef.current) return;

    // Set the value of the textarea before scrolling to the bottom setPolicy
    // is an async function so it might not have updated the value before
    textareaRef.current.value = policy ? `${policy}\n\n${text}` : text;
    textareaRef.current.scroll({
      top: textareaRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  const handleSave = async () => {
    const isExistingName = policies.some(
      (policy) => policy.name === policyName && editPolicy?.name !== policyName,
    );
    if (isExistingName) {
      toast({
        variant: "destructive",
        title: "Policy name already exists, please choose a different name.",
      });
      return;
    }

    const departmentIds: number[] = selectedDepartments
      .map((d) => d.id)
      .filter((id) => id !== null) as number[];

    try {
      const { data } = await upsertPolicy({
        variables: {
          name: policyName,
          policy,
          departmentIds,
          isActive: true,
          uuid: editPolicy?.uuid,
        },
      });
      if (data?.upsertPolicy.__typename !== "MutationUpsertPolicySuccess") {
        throw new Error();
      }
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to save policy, please try again",
      });
    }
  };

  const departments: DepartmentOption[] =
    departmentsData?.departments?.__typename === "QueryDepartmentsSuccess"
      ? departmentsData.departments.data.departments
      : [];

  const saveDisabled =
    !policyName || !policy || selectedDepartments.length === 0 || loading;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex flex-col bg-black  px-14 pb-12 pt-8">
        <div className="flex justify-end">
          <X className="size-6" onClick={onClose} />
        </div>
        <div className="mb-4 mt-8 flex w-full grow">
          <div className="flex h-full grow flex-col rounded-md bg-[#1B1B1B] p-6 ">
            <div className="mb-4 flex gap-x-4">
              <Input
                className="bg-[#2b2b2b]"
                value={policyName}
                onChange={(e) => setPolicyName(e.target.value)}
                placeholder="Enter policy name"
              />
              <SelectDepartment
                options={departments}
                value={selectedDepartments}
                setValue={setSelectedDepartments}
                variant="dark"
              />
            </div>
            <Textarea
              ref={textareaRef}
              className="flex-grow resize-none border-white/10 scrollbar-thin scrollbar-track-[#ffffffcc] scrollbar-thumb-[#1c1c1c80]"
              value={policy}
              placeholder={placheholder}
              onChange={(e) => setPolicy(e.target.value)}
            />
          </div>
          <div className="ml-4 h-full min-w-[35vw] max-w-[35vw] rounded-md bg-[#1B1B1B] p-6">
            <SuggestionBox appendText={appentText} />
            <TemplateSection appendText={appentText} />
          </div>
        </div>
        <div className="flex w-full justify-end">
          <Button
            className="mr-3"
            disabled={!policy || loading}
            onClick={() => setIsTestPolicyOpen(true)}
          >
            Test Policy
          </Button>
          <Button disabled={saveDisabled} onClick={handleSave}>
            Save Policy
          </Button>
        </div>
      </div>
      {isTestPolicyOpen && (
        <Sheet
          open
          onOpenChange={(open) => !open && setIsTestPolicyOpen(false)}
        >
          <TestPolicy
            policy={policy}
            onClose={() => setIsTestPolicyOpen(false)}
          />
        </Sheet>
      )}
    </Portal>
  );
};
